// @ts-nocheck
import Dots from '@jo-v1/components/Loading/Dots';
import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CareerPaths from '@core/blocks/jo-v3/components/CareerPaths';
import { getCategoryByName } from '@core/blocks/jo-v3/utils/getCategoryByName';
import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import { selectDcsCategories } from '@core/reducers/dcsSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import { removeProgramFromList, selectLeadDelivery } from '@core/reducers/matchesSlice';
import SponsoredResultsPopover from '@core/shared/components/SponsoredResultsPopover';
import type { Qualifications, VoyagerResult } from '@core/ts/results';

import DecisionFactorsFlags from './DecisionFactorsFlags';
import styles from './ProgramSelect.module.css';
import RecommenderCard from './RecommenderCard';

type Props = {
  results: VoyagerResult[];
  setRecommenderStep: (step: string) => void;
  closePopup: () => void;
  eventingOverrides: { [key: string]: any };
  qualifications?: Qualifications;
};

const ProgramSelect: FC<Props> = ({ results, setRecommenderStep, closePopup, eventingOverrides, qualifications }) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // current programs from store
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // inputs state properties
  const inputs = useSelector(selectAllInputs);
  const category = useSelector(selectDcsCategories)[0];
  const industryData = getCategoryByName(category);

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: 'exit-element',
      text: 'Skip to all results',
      location: eventingOverrides?.location,
      htmlId: 'thank-you-button',
      name: 'skip-recommender',
    },
  });

  const onClose = () => {
    // if there are any selections and user closes the modal, we remove them from the currentPrograms list
    for (const current of currentPrograms) {
      dispatch(removeProgramFromList(current));
    }
    if (closePopup) closePopup();
    elementClicked();
  };

  const singleHit = results.length === 1;

  if (loading) {
    return (
      <section className={styles.loadingContainer}>
        <h2>Loading...</h2>
        <Dots />
      </section>
    );
  }

  return (
    <section className={styles.programSelect}>
      <div className={styles.content}>
        <header>
          <p className={styles.personalization}>
            {inputs.firstName?.value ? `You've got matches, ${inputs.firstName.value}!` : "You've got matches!"}
          </p>
          {singleHit ? (
            <>
              <h2 id="recommender_title" className={styles.title}>
                This is your top recommendation:
              </h2>
            </>
          ) : (
            <h2 id="recommender_title" className={styles.title}>
              These are your top recommendations:
            </h2>
          )}

          <DecisionFactorsFlags decisionFactors={inputs['decision-factors']} />
        </header>

        <div id="recommender_results" className={styles.cards}>
          {results.map((result: any, index: number) => (
            <RecommenderCard
              index={index}
              setRecommenderStep={setRecommenderStep}
              key={result.program.id}
              position={index + 1}
              result={result}
              setLoading={setLoading}
              eventingOverrides={{
                location: eventingOverrides?.location,
                customDimensions: [
                  { key: 'isUserRequested', value: 'true' },
                  ...(eventingOverrides.customDimensions || []),
                ],
              }}
            />
          ))}
          {singleHit && <CareerPaths industryData={industryData} hideTitle={false} />}
        </div>
      </div>

      <div className={styles.actions}>
        <button type="button" onClick={onClose} className={styles.skipToResults}>
          Skip to all results
        </button>
        <div className={styles.sponsoredResultsPopover}>
          <SponsoredResultsPopover position="above" withIcon={false} />
        </div>
      </div>
    </section>
  );
};

export default ProgramSelect;
